/**
 * useUnifiedTalentSearch — Round 77 (A-C-08)
 *
 * UI/orchestration bridge over two existing, separate RPCs:
 *   - in_scope mode → useInScopeTalent (Floo hierarchy, R73)
 *   - discover mode → useVisibleTalent (Connect, unchanged)
 *
 * NOT a backend merge. Only one underlying query is enabled at a time
 * (the other is gated by `enabled:false`). Results are lightly normalized
 * into a unified shape so the directory can render either source.
 *
 * Privacy:
 *   - in_scope rows never receive Connect-only fields
 *   - discover rows never receive in_scope-only fields
 *   - user_private fields are never surfaced here
 */
import { useMemo } from 'react';
import { useInScopeTalent, type InScopeScopeType, type InScopeTalentRow } from './useInScopeTalent';
import { useVisibleTalent, type TalentCard } from './useConnectApi';
import { useEnterpriseScope } from './useEnterpriseScope';
import { useAuth } from '@/contexts/AuthContext';

export type UnifiedMode = 'in_scope' | 'discover';
export type UnifiedSource = 'in_scope' | 'connect';

export interface UnifiedTalentResult {
  user_id: string;
  display_name: string;
  shop_name: string | null;
  role: string;
  technician_profile_id: string | null;
  source: UnifiedSource;
  // mode-specific extras
  open_to_work_status?: string;     // discover
  distance_miles?: number | null;   // discover
  scope_label?: string;             // in_scope ("Organization" / "Region" / ...)
  technician_type_title?: string;
  shop_id?: string;                 // in_scope (used as a stable list key)
}

interface Args {
  mode: UnifiedMode;
  scopeType: InScopeScopeType | null;
  scopeId: string | null;
  filters?: {
    shopId?: string;          // in_scope only
    role?: string;            // both modes
    search?: string;          // both modes
  };
}

const ROLE_TO_CONNECT: Record<string, string> = {
  TECHNICIAN: 'technician',
  ADVISOR: 'advisor',
  MANAGER: 'manager',
  STUDENT: 'student',
};

export function useUnifiedTalentSearch({ mode, scopeType, scopeId, filters }: Args) {
  const { canViewInScopeTalent, scopeLabel } = useEnterpriseScope();
  const { activeShop } = useAuth();

  // In-scope query — disabled in discover mode
  const inScope = useInScopeTalent({
    scopeType: mode === 'in_scope' ? scopeType : null,
    scopeId: mode === 'in_scope' ? scopeId : null,
  });

  // Connect query — disabled in in_scope mode (and gated by activeShop inside the hook)
  const connectRoleFilter = mode === 'discover' && filters?.role
    ? [ROLE_TO_CONNECT[filters.role] ?? filters.role.toLowerCase()]
    : undefined;
  const connect = useVisibleTalent(mode === 'discover' ? connectRoleFilter : undefined);

  const needsActiveShop = mode === 'discover' && !activeShop?.id;

  const results = useMemo<UnifiedTalentResult[]>(() => {
    if (mode === 'in_scope') {
      const rows = (inScope.data ?? []) as InScopeTalentRow[];
      const search = filters?.search?.trim().toLowerCase() ?? '';
      return rows
        .filter((r) => {
          if (filters?.shopId && filters.shopId !== 'all' && r.shop_id !== filters.shopId) return false;
          if (filters?.role && filters.role !== 'all' && r.role !== filters.role) return false;
          if (search && !r.display_name.toLowerCase().includes(search) && !r.shop_name.toLowerCase().includes(search)) {
            return false;
          }
          return true;
        })
        .map<UnifiedTalentResult>((r) => ({
          user_id: r.user_id,
          display_name: r.display_name,
          shop_name: r.shop_name,
          shop_id: r.shop_id,
          role: r.role,
          technician_profile_id: r.technician_profile_id,
          source: 'in_scope',
          scope_label: scopeLabel,
          technician_type_title: r.technician_type_title,
        }));
    }

    // discover
    const rows = (connect.data ?? []) as TalentCard[];
    const search = filters?.search?.trim().toLowerCase() ?? '';
    return rows
      .filter((r) => {
        if (search && !r.display_name.toLowerCase().includes(search)) return false;
        return true;
      })
      .map<UnifiedTalentResult>((r) => ({
        user_id: r.user_id,
        display_name: r.display_name,
        shop_name: null,
        role: r.roles?.[0] ?? '',
        technician_profile_id: r.technician_profile_id,
        source: 'connect',
        open_to_work_status: r.open_to_work_status,
        distance_miles: r.distance_miles,
        technician_type_title: r.technician_type_title,
      }));
  }, [mode, inScope.data, connect.data, filters?.shopId, filters?.role, filters?.search, scopeLabel]);

  const isLoading = mode === 'in_scope' ? inScope.isLoading : connect.isLoading;
  const error = (mode === 'in_scope' ? inScope.error : connect.error) as Error | null;

  return {
    results,
    isLoading,
    error,
    needsActiveShop,
    canViewInScope: canViewInScopeTalent,
    rawInScope: mode === 'in_scope' ? (inScope.data ?? []) : [],
  };
}
